/* eslint-disable */
/*
 * Atlas chart runtime — the one place that turns a stored Atlas chart config into what is drawn.
 *
 * Used by BOTH renderers so chat and PDF exports match:
 *   - atlas-backend: inlined into the headless page built by viz_renderer.build_chart_html (window.AtlasChartRuntime)
 *   - client: copied verbatim to src/components/atlas/charts/atlasChartRuntime.js (module.exports)
 * Keep the two copies identical. The canonical copy lives in atlas-backend app/atlas/tools/viz_assets/.
 *
 * Input: a Chart.js config written by create_data_chart_impl, carrying `config.meta` (version >= 1):
 *   meta.title / subtitle / footnote          text drawn by the card, outside the canvas
 *   meta.y_format / x_format / y1_format      value formats (see FORMATS)
 *   meta.series[i] = {label, role, mark, color_locked}   index-aligned with data.datasets
 *   meta.annotations = [{series, index, text}]
 *   meta.horizontal / stacked / chart_type
 * The stored datasets already carry light-theme colors; prepare() re-resolves them per theme from the roles.
 * No Chart.js import and no Chart.defaults changes: the caller passes the result to `new Chart(ctx, cfg)`.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.AtlasChartRuntime = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var SYS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  // ---------------------------------------------------------------- tokens (validated palettes; see docs/atlas-viz-style.md)
  var THEMES = {
    light: {
      surface: '#ffffff', ink: '#111827', ink2: '#4b5563', muted: '#6b7280', border: 'rgba(60,60,67,0.12)',
      grid: 'rgba(17,24,39,0.08)', gridPrint: 'rgba(17,24,39,0.13)', axis: 'rgba(17,24,39,0.24)', tick: '#6b7280',
      roles: {
        subject: '#E26313', peer: '#2a78d6', peer2: '#1baf7a', context: '#9ca3af', reference: '#6b7280', other: '#9ca3af',
        ord1: '#86b6ef', ord2: '#3987e5', ord3: '#1c5cab', ord4: '#0d366b',
        cat1: '#2a78d6', cat2: '#E26313', cat3: '#1baf7a', cat4: '#eda100', cat5: '#e87ba4', cat6: '#008300', cat7: '#4a3aa7', cat8: '#e34948'
      }
    },
    dark: {
      surface: '#222226', ink: '#f9fafb', ink2: '#d1d5db', muted: '#9ca3af', border: 'rgba(255,255,255,0.10)',
      grid: 'rgba(255,255,255,0.07)', gridPrint: 'rgba(255,255,255,0.12)', axis: 'rgba(255,255,255,0.22)', tick: '#9ca3af',
      roles: {
        subject: '#E26313', peer: '#3987e5', peer2: '#199e70', context: '#6b7280', reference: '#9ca3af', other: '#6b7280',
        ord1: '#256abf', ord2: '#3987e5', ord3: '#86b6ef', ord4: '#cde2fb',
        cat1: '#3987e5', cat2: '#E26313', cat3: '#199e70', cat4: '#c98500', cat5: '#d55181', cat6: '#008300', cat7: '#9085e9', cat8: '#e66767'
      }
    }
  };

  function theme(name) { return THEMES[name] || THEMES.light; }
  function roleColor(t, role) { return (role && t.roles[role]) || t.roles.context; }

  // ---------------------------------------------------------------- formats
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function trim0(s) { return String(s).replace(/\.0+$/, '').replace(/(\.\d*?[1-9])0+$/, '$1'); }
  function isNum(v) { return typeof v === 'number' && isFinite(v); }
  function toNum(v) { if (v === null || v === undefined || v === '') return null; var n = Number(v); return isFinite(n) ? n : null; }
  function grouped(n) { return Math.round(n).toLocaleString('en-US'); }
  function pctText(p) { var a = Math.abs(p); return (a < 100 && Math.abs(p % 1) > 1e-9 ? p.toFixed(1) : String(Math.round(p))) + '%'; }
  function sign(v, s) { return v < 0 ? '-' + s : s; }

  var FORMATS = {
    money: function (v) { return sign(v, '$' + grouped(Math.abs(v))); },
    money_compact: function (v) {
      var a = Math.abs(v);
      if (a >= 1e9) return sign(v, '$' + trim0((a / 1e9).toFixed(2)) + 'B');
      if (a >= 1e6) return sign(v, '$' + trim0((a / 1e6).toFixed(2)) + 'M');
      if (a >= 1e4) return sign(v, '$' + Math.round(a / 1e3) + 'k');
      if (a >= 1e3) return sign(v, '$' + trim0((a / 1e3).toFixed(1)) + 'k');
      return sign(v, '$' + Math.round(a));
    },
    percent: function (v) { return pctText(v); },
    ratio_percent: function (v) { return pctText(v * 100); },
    number: function (v) { var a = Math.abs(v); return a >= 100 || Math.abs(v % 1) < 1e-9 ? grouped(v) : trim0(v.toFixed(a < 10 ? 2 : 1)); },
    compact: function (v) {
      var a = Math.abs(v);
      if (a >= 1e9) return sign(v, trim0((a / 1e9).toFixed(1)) + 'B');
      if (a >= 1e6) return sign(v, trim0((a / 1e6).toFixed(1)) + 'M');
      if (a >= 1e4) return sign(v, Math.round(a / 1e3) + 'k');
      if (a >= 1e3) return sign(v, trim0((a / 1e3).toFixed(1)) + 'k');
      return FORMATS.number(v);
    },
    decimal: function (v) { return v.toFixed(2); },
    days: function (v) { return Math.round(v) + ' d'; }
  };
  FORMATS.currency = FORMATS.money_compact; // legacy alias used by map configs

  function format(kind, v) {
    var n = toNum(v);
    if (n === null) return '—';
    return (FORMATS[kind] || FORMATS.number)(n);
  }

  /** Axis ticks stay short: large full-dollar or count values are shown compact ($600k, 24k); tooltips keep full values. */
  function tickFormat(kind, v) {
    var n = toNum(v);
    if (n !== null && Math.abs(n) >= 1e5) {
      if (kind === 'money') return FORMATS.money_compact(n);
      if (kind === 'number') return FORMATS.compact(n);
    }
    return format(kind, v);
  }

  function formatX(kind, s) {
    if (s === null || s === undefined) return '';
    var str = String(s);
    if (kind === 'month' && /^\d{4}-\d{2}/.test(str)) return MON[+str.slice(5, 7) - 1] + ' ' + str.slice(0, 4);
    if (kind === 'date' && /^\d{4}-\d{2}-\d{2}/.test(str)) return MON[+str.slice(5, 7) - 1] + ' ' + (+str.slice(8, 10)) + ', ' + str.slice(0, 4);
    if (FORMATS[kind] && toNum(s) !== null) return format(kind, s);
    return str;
  }

  function lastIndex(arr) { for (var i = (arr || []).length - 1; i >= 0; i--) if (valueOf(arr[i]) !== null) return i; return -1; }
  function valueOf(p) { if (p && typeof p === 'object') return toNum(p.y); return toNum(p); }

  // ---------------------------------------------------------------- plugins (instances, never registered globally)
  var endMarks = {
    id: 'atlasEndMarks',
    afterDatasetsDraw: function (chart, _args, o) {
      if (!o || !o.enabled) return;
      var ctx = chart.ctx, pts = [];
      chart.data.datasets.forEach(function (ds, i) {
        if (!ds._atlasEnd) return;
        var meta = chart.getDatasetMeta(i);
        if (meta.hidden) return;
        var k = lastIndex(ds.data); if (k < 0) return;
        var el = meta.data[k]; if (!el) return;
        pts.push({ x: el.x, y: el.y, c: ds.borderColor, text: ds._atlasEndText, subj: ds._atlasRole === 'subject' });
      });
      var s = null; pts.forEach(function (p) { if (p.subj) s = p; });
      ctx.save();
      pts.forEach(function (p) {
        ctx.beginPath(); ctx.arc(p.x, p.y, o.dot, 0, Math.PI * 2); ctx.fillStyle = p.c; ctx.fill();
        ctx.lineWidth = 2; ctx.strokeStyle = o.surface; ctx.stroke();
        if (!p.text) return;
        if (!p.subj && s && Math.abs(s.y - p.y) < o.font + 4) return;
        ctx.font = (p.subj ? '600 ' : '500 ') + o.font + 'px ' + (o.family || SYS);
        ctx.fillStyle = p.subj ? o.ink : o.muted; ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
        ctx.fillText(p.text, p.x + o.dot + 4, p.y);
      });
      (o.notes || []).forEach(function (n) {
        var meta = chart.getDatasetMeta(n.series); if (!meta || meta.hidden) return;
        var el = meta.data[n.index]; if (!el) return;
        ctx.beginPath(); ctx.arc(el.x, el.y, 3, 0, Math.PI * 2); ctx.fillStyle = o.ink; ctx.fill();
        ctx.strokeStyle = o.muted; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(el.x, el.y - 5); ctx.lineTo(el.x, el.y - 15); ctx.stroke();
        ctx.font = '500 ' + (o.font - 0.5) + 'px ' + (o.family || SYS); ctx.fillStyle = o.ink2; ctx.textBaseline = 'bottom';
        var w = ctx.measureText(n.text).width, area = chart.chartArea;
        var align = el.x - w / 2 < area.left ? 'left' : el.x + w / 2 > area.right + 40 ? 'right' : 'center';
        ctx.textAlign = align;
        ctx.fillText(n.text, el.x, el.y - 18);
      });
      ctx.restore();
    }
  };

  var crosshair = {
    id: 'atlasCrosshair',
    afterDatasetsDraw: function (chart, _args, o) {
      if (!o || !o.enabled || !chart.tooltip || !chart.tooltip.getActiveElements) return;
      var act = chart.tooltip.getActiveElements(); if (!act.length) return;
      var x = act[0].element.x, a = chart.chartArea, ctx = chart.ctx;
      ctx.save(); ctx.strokeStyle = o.color; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(x, a.top); ctx.lineTo(x, a.bottom); ctx.stroke(); ctx.restore();
    }
  };

  // ---------------------------------------------------------------- helpers
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function hasMeta(config) { return !!(config && config.meta && config.meta.version >= 1); }
  function seriesMeta(config, i) { return ((config.meta && config.meta.series) || [])[i] || {}; }
  function isCartesian(type) { return ['line', 'bar', 'scatter', 'bubble'].indexOf(type) >= 0; }

  function monthTickCallback(labels) {
    var n = labels.length, step = n > 96 ? 2 : 1;
    return function (_v, i) {
      var l = String(labels[i] || '');
      if (n <= 18) return /^\d{4}-\d{2}/.test(l) ? MON[+l.slice(5, 7) - 1] + " '" + l.slice(2, 4) : l;
      return /^\d{4}-01/.test(l) && (+l.slice(0, 4)) % step === 0 ? l.slice(0, 4) : null;
    };
  }

  /**
   * prepare(config, {theme, print, font}) -> a fresh Chart.js config ready for `new Chart()`.
   * `font` overrides the font family (the PDF renderer passes its loaded web font).
   * Configs without meta are returned as a deep copy (callers should use the legacy renderer for those).
   */
  function prepare(config, opts) {
    opts = opts || {};
    var cfg = clone(config);
    if (!hasMeta(cfg)) return cfg;
    var t = theme(opts.theme), print = !!opts.print, m = cfg.meta;
    var labels = (cfg.data && cfg.data.labels) || [];
    var horizontal = !!m.horizontal, type = cfg.type;
    var monthly = m.x_format === 'month' && labels.length > 0;
    var valueAxis = horizontal ? 'x' : 'y', catAxis = horizontal ? 'y' : 'x';
    var fontPx = 11, family = opts.font || SYS;

    // datasets: re-resolve colors from roles for this theme
    (cfg.data.datasets || []).forEach(function (ds, i) {
      var sm = seriesMeta(cfg, i), role = sm.role || ds.role || 'context';
      var mark = sm.mark || (ds.type || type);
      if (mark === 'slice') {
        var roles = sm.slice_roles || [];
        ds.backgroundColor = roles.map(function (r) { return roleColor(t, r); });
        ds.borderColor = t.surface; ds.borderWidth = 2;
        return;
      }
      var c = sm.color_locked && ds.borderColor ? ds.borderColor : roleColor(t, role);
      ds._atlasRole = role;
      if (mark === 'line' || mark === 'area') {
        ds.borderColor = c; ds.pointBackgroundColor = c; ds.pointHoverBackgroundColor = c; ds.pointHoverBorderColor = t.surface;
        ds.backgroundColor = mark === 'area' ? hexA(c, role === 'subject' ? 0.14 : 0.10) : c;
        ds._atlasEnd = m.end_labels !== false && role !== 'reference' && !horizontal && labels.length > 1;
        ds._atlasEndText = ds._atlasEnd && (role === 'subject' || (sm.end_label === true)) ? format(m.y_format, valueOf(ds.data[lastIndex(ds.data)])) : null;
      } else if (mark === 'tick') {
        ds.borderColor = t.ink2; ds.backgroundColor = t.ink2; ds.pointBackgroundColor = t.ink2;
      } else if (mark === 'point' || mark === 'bubble') {
        ds.backgroundColor = hexA(c, 0.78); ds.borderColor = t.surface; ds.hoverBackgroundColor = c;
      } else {
        ds.backgroundColor = c; ds.hoverBackgroundColor = c; ds.borderColor = t.surface;
        if (Array.isArray(sm.point_roles)) {
          ds.backgroundColor = sm.point_roles.map(function (r) { return roleColor(t, r); });
          ds.hoverBackgroundColor = ds.backgroundColor;
        }
      }
    });

    var o = cfg.options = cfg.options || {};
    o.responsive = true; o.maintainAspectRatio = false;
    if (print) o.animation = false;
    o.plugins = o.plugins || {};
    o.plugins.title = { display: false }; o.plugins.subtitle = { display: false }; o.plugins.legend = { display: false };
    var hasLine = (cfg.data.datasets || []).some(function (ds) { return ds._atlasEnd; });
    // Room for the longest end label ("$475,000" needs more than "3.9%"): ~6.6 px per character at 11.5 px.
    var endChars = 0;
    (cfg.data.datasets || []).forEach(function (ds) { if (ds._atlasEndText) endChars = Math.max(endChars, String(ds._atlasEndText).length); });
    var rightPad = hasLine ? Math.max(24, Math.round(endChars * 6.6 + 18)) : 12;
    o.layout = { padding: { top: (m.annotations && m.annotations.length) ? 22 : 6, right: rightPad, left: 2, bottom: 0 } };
    o.plugins.atlasEndMarks = { enabled: true, surface: t.surface, ink: t.ink, ink2: t.ink2, muted: t.muted, font: 11.5, dot: 4, family: family, notes: m.annotations || [] };
    o.plugins.atlasCrosshair = { enabled: !print && hasLine, color: t.axis };

    var yf = m.y_format, xf = m.x_format;
    if (isCartesian(type)) {
      var point = type === 'scatter' || type === 'bubble';
      o.interaction = point ? { mode: 'nearest', intersect: true } : { mode: 'index', intersect: false, axis: horizontal ? 'y' : 'x' };
      o.plugins.tooltip = tooltip(t, family, function (items) {
        if (!items.length || point) return '';
        return formatX(xf, labels[items[0].dataIndex]);
      }, function (c) {
        var fmt = c.dataset.yAxisID === 'y1' && m.y1_format ? m.y1_format : yf;
        if (point) {
          var raw = c.raw || {}, name = raw.label || raw.name || c.dataset.label;
          return name + ':  ' + formatX(xf, c.parsed.x) + ' · ' + format(yf, c.parsed.y);
        }
        return format(fmt, horizontal ? c.parsed.x : c.parsed.y) + '  ' + c.dataset.label;
      });
      var scales = o.scales = o.scales || {};
      Object.keys(scales).forEach(function (k) {
        var s = scales[k] || {};
        s.ticks = s.ticks || {};
        s.ticks.color = t.tick; s.ticks.font = { family: family, size: fontPx };
        if (s.title) { s.title.display = false; }
        if (k === catAxis && !point) {
          s.grid = { display: false }; s.border = { color: t.axis };
          s.ticks.maxRotation = 0; s.ticks.padding = 6;
          if (monthly && !horizontal) { s.ticks.autoSkip = false; s.ticks.callback = monthTickCallback(labels); }
          else { s.ticks.callback = (function (lbls) { return function (_v, i) { return lbls[i]; }; })(labels); s.ticks.autoSkip = !horizontal; }
        } else if (k === 'y1') {
          s.grid = { drawOnChartArea: false }; s.border = { display: false };
          s.ticks.maxTicksLimit = 5; s.ticks.callback = (function (f) { return function (v) { return tickFormat(f, v); }; })(m.y1_format || yf);
        } else {
          s.grid = { color: print ? t.gridPrint : t.grid, drawTicks: false }; s.border = { display: false };
          s.ticks.maxTicksLimit = point ? 6 : 5; s.ticks.padding = 8;
          var f = k === 'x' && point ? xf : yf;
          if (point && k === 'x' && f && !FORMATS[f]) f = null;
          if (f) s.ticks.callback = (function (ff) { return function (v) { return tickFormat(ff, v); }; })(f);
        }
      });
      if (!scales[valueAxis]) scales[valueAxis] = {};
    } else {
      // pie / doughnut / radar / polarArea
      o.plugins.tooltip = tooltip(t, family, function () { return ''; }, function (c) {
        return (c.label ? c.label + ':  ' : '') + format(yf, c.raw);
      });
      if (type === 'radar' && o.scales && o.scales.r) {
        o.scales.r.grid = { color: t.grid }; o.scales.r.angleLines = { color: t.grid };
        o.scales.r.pointLabels = { color: t.ink2, font: { family: family, size: fontPx } };
        o.scales.r.ticks = { color: t.tick, backdropColor: 'transparent', font: { family: family, size: 10 } };
      }
    }
    cfg.plugins = [endMarks, crosshair];
    return cfg;
  }

  function tooltip(t, family, title, label) {
    return {
      enabled: true, backgroundColor: t.surface, borderColor: t.border, borderWidth: 1,
      titleColor: t.ink, bodyColor: t.ink, padding: 10, cornerRadius: 8, boxPadding: 5, boxWidth: 9, boxHeight: 9,
      usePointStyle: true, titleFont: { family: family, size: 12, weight: '600' }, bodyFont: { family: family, size: 12 },
      itemSort: function (a, b) { return (a.dataset._atlasRole === 'subject' ? -1 : 0) - (b.dataset._atlasRole === 'subject' ? -1 : 0); },
      callbacks: {
        title: title, label: label,
        labelPointStyle: function () { return { pointStyle: 'circle', rotation: 0 }; },
        labelColor: function (c) {
          var col = Array.isArray(c.dataset.backgroundColor) ? c.dataset.backgroundColor[c.dataIndex] : (c.dataset.type === 'line' || c.chart.config.type === 'line' ? c.dataset.borderColor : c.dataset.backgroundColor);
          return { borderColor: col, backgroundColor: col };
        }
      }
    };
  }

  function hexA(hex, a) {
    var h = String(hex || '').replace('#', '');
    if (h.length !== 6) return hex;
    return 'rgba(' + parseInt(h.slice(0, 2), 16) + ',' + parseInt(h.slice(2, 4), 16) + ',' + parseInt(h.slice(4, 6), 16) + ',' + a + ')';
  }

  // ---------------------------------------------------------------- card pieces
  /** Legend items for the card: [{label, color, mark: 'line'|'dash'|'rect'|'tick'|'dot'}]. Empty for one series. */
  function legend(config, themeName) {
    if (!hasMeta(config)) return [];
    var t = theme(themeName), m = config.meta, ds = (config.data && config.data.datasets) || [];
    if (m.chart_type === 'pie' || m.chart_type === 'doughnut') {
      var roles = (m.series[0] && m.series[0].slice_roles) || [];
      return (config.data.labels || []).map(function (l, i) { return { label: String(l), color: roleColor(t, roles[i]), mark: 'rect' }; });
    }
    if (ds.length < 2) return [];
    return ds.map(function (d, i) {
      var sm = seriesMeta(config, i), mark = sm.mark || d.type || config.type;
      var color = sm.color_locked && d.borderColor ? d.borderColor : roleColor(t, sm.role);
      var key = mark === 'line' || mark === 'area' ? (sm.role === 'reference' ? 'dash' : 'line') : mark === 'tick' ? 'tick' : (mark === 'point' || mark === 'bubble') ? 'dot' : 'rect';
      return { label: d.label || '', color: mark === 'tick' ? t.ink2 : color, mark: key };
    });
  }

  /** Table twin: {columns: [...], rows: [[...]]}, values formatted exactly like the ticks. null when not tabular. */
  function table(config) {
    if (!hasMeta(config)) return null;
    var m = config.meta, ds = (config.data && config.data.datasets) || [], labels = (config.data && config.data.labels) || [];
    if (config.type === 'scatter' || config.type === 'bubble') {
      var rowsP = [];
      ds.forEach(function (d) { (d.data || []).forEach(function (p) { rowsP.push([p.label || d.label || '', formatX(m.x_format, p.x), format(m.y_format, p.y)]); }); });
      return { columns: ['', 'x', 'y'], rows: rowsP.slice(0, 500) };
    }
    if (!labels.length) return null;
    var rows = [];
    labels.forEach(function (l, i) {
      var vals = ds.map(function (d) { return valueOf((d.data || [])[i]); });
      if (vals.every(function (v) { return v === null; })) return; // a row with no values says nothing
      rows.push([formatX(m.x_format, l)].concat(ds.map(function (d, j) {
        var f = d.yAxisID === 'y1' && m.y1_format ? m.y1_format : m.y_format;
        return format(f, vals[j]);
      })));
    });
    if (m.x_format === 'month' || m.x_format === 'date' || m.x_format === 'quarter') rows.reverse();
    return { columns: [m.x_label || ''].concat(ds.map(function (d) { return d.label || ''; })), rows: rows };
  }

  /** Suggested canvas height in CSS px for the card body. */
  function bodyHeight(config, opts) {
    var m = (config && config.meta) || {}, n = ((config && config.data && config.data.labels) || []).length;
    if (m.horizontal) return Math.max(180, Math.min(620, n * 28 + 44));
    if (m.chart_type === 'pie' || m.chart_type === 'doughnut') return 240;
    return opts && opts.print ? 250 : 270;
  }

  return {
    version: 1, THEMES: THEMES, FORMATS: FORMATS, SYS: SYS,
    hasMeta: hasMeta, prepare: prepare, legend: legend, table: table, bodyHeight: bodyHeight,
    format: format, formatX: formatX, roleColor: function (role, themeName) { return roleColor(theme(themeName), role); },
    plugins: { endMarks: endMarks, crosshair: crosshair }
  };
});
